import api from './api';
import { getVersion as getLocalVersion, getVersionInfo as getLocalVersionInfo } from '../version';

/**
 * Gets the API version from the server
 * @returns {Promise<string|null>} Version string or null if error
 */
export const getApiVersion = async () => {
    try {
        const response = await api.get('/api/version');
        return response.data.version;
    } catch (error) {
        console.error('Error fetching API version:', error);
        return null;
    }
};

/**
 * Gets the client version
 * @returns {string} Client version string
 */
export const getClientVersion = () => {
    return getLocalVersion();
};

/**
 * Gets both client and API versions
 * @returns {Promise<{client: string, api: string|null}>} Object with client and API versions
 */
export const getAllVersions = async () => {
    const clientVersion = getClientVersion();
    const apiVersion = await getApiVersion();
    
    return {
        client: clientVersion,
        api: apiVersion
    };
};

/**
 * Gets detailed client version information
 * @returns {Object} Client version information
 */
export const getClientVersionInfo = () => {
    return getLocalVersionInfo();
};

/**
 * Gets the current API environment (prod, staging, dev)
 * @returns {Promise<string|null>} Environment name or null if error
 */
export const getEnvironment = async () => {
    try {
        const response = await api.get('/api/version');
        return response.data.environment || null;
    } catch (error) {
        console.error('Error fetching environment:', error);
        return null;
    }
};

/**
 * Gets the version information from the API
 * This is the preferred method for getting version information
 * @returns {Promise<{version: string, environment: string, build_date: string}>} Version information
 */
export const getVersionInfo = async () => {
    try {
        const response = await api.get('/api/version');
        return response.data;
    } catch (error) {
        console.error('Error fetching version info:', error);
        return { 
            version: null, 
            environment: null,
            build_date: null 
        };
    }
};

// For backward compatibility - now returns a Promise for compatibility with existing code
// This should now use the API version instead of the client version
export const getVersion = async () => {
    try {
        const response = await api.get('/api/version');
        return response.data.version;
    } catch (error) {
        console.error('Error fetching API version:', error);
        // Fall back to client version only if API call fails
        return getClientVersion();
    }
};
